// client/src/coms/accounts/AccountSwitcher.js
// Account menu opened from the user bar: accounts saved on this device, switching, default account, removing an
// account from the device (not deleting it), adding another account and signing out.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import LoginButton from '../components/LoginButton';
import { getActiveGame } from '../views/games_parts/activeGameStore';
import QuickStatus from '../presence/QuickStatus';
import {
  announceAccountChange,
  listAccounts,
  listInterruptions,
  removeAccount,
  restartApp,
  setDefaultAccount,
  signOutAllAccounts,
  switchAccount,
} from './accountsApi';
import './accounts.css';

export function AccountAvatar({ url, name, size = 32 }) {
  const [broken, setBroken] = useState(false);
  const initial = String(name || '?').trim().slice(0, 1).toUpperCase() || '?';
  return (
    <span className="vx-acct-avatar" style={{ width: size, height: size, fontSize: Math.round(size * 0.42) }} aria-hidden="true">
      {url && !broken ? <img src={url} alt="" onError={() => setBroken(true)} /> : initial}
    </span>
  );
}

function sentence(list) {
  if (list.length <= 1) return list[0] || '';
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

export default function AccountSwitcher({ open, anchorRef, onClose, currentUser, voiceSession, onOpenSettings, onOpenStatusSettings }) {
  const [accounts, setAccounts] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [confirm, setConfirm] = useState(null); // { kind: 'switch'|'remove'|'signout-all', account, message }
  const [rowMenu, setRowMenu] = useState(null); // user_id
  const [pos, setPos] = useState({ left: 12, bottom: 70, top: null });
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await listAccounts();
      setAccounts(Array.isArray(res.accounts) ? res.accounts : []);
      setError('');
    } catch (e) {
      setAccounts([]);
      setError('Could not load your saved accounts.');
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setConfirm(null);
    setRowMenu(null);
    setAccounts(null);
    load();
  }, [open, load]);

  useLayoutEffect(() => {
    if (!open || !anchorRef || !anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    const width = Math.min(340, window.innerWidth - 16);
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
    const below = r.top < window.innerHeight / 2;
    setPos(below ? { left, top: r.bottom + 6, bottom: null, width } : { left, bottom: window.innerHeight - r.top + 6, top: null, width });
  }, [open, anchorRef]);

  // Focus the menu on open; Escape closes and returns focus; Tab stays inside.
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => {
      const first = panelRef.current && panelRef.current.querySelector('button:not([disabled])');
      if (first) first.focus();
    }, 0);
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (confirm) setConfirm(null);
        else onClose();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const els = Array.from(panelRef.current.querySelectorAll('button:not([disabled]), [href], input, select'));
      if (!els.length) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    const onDown = (e) => {
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      if (anchorRef && anchorRef.current && anchorRef.current.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onDown, true);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mousedown', onDown, true);
    };
  }, [open, confirm, onClose, anchorRef]);

  useEffect(() => {
    if (open || !anchorRef || !anchorRef.current) return;
    // Returning focus to the trigger when the menu closes.
    if (panelRef.current && panelRef.current.contains(document.activeElement)) anchorRef.current.focus();
  }, [open, anchorRef]);

  const doSwitch = async (account) => {
    setBusy(`switch:${account.user_id}`);
    setError('');
    try {
      await switchAccount(account.user_id);
      announceAccountChange(account.user_id);
      restartApp('/comms');
    } catch (e) {
      setBusy('');
      if (e.code === 'reauth_required') {
        setError(`Your sign-in for ${account.username} has expired. Use “Sign in again” below.`);
        load();
      } else setError(e.message || 'Could not switch account.');
    }
  };

  const requestSwitch = (account) => {
    if (account.is_current || busy) return;
    if (account.expired) {
      setError(`Your sign-in for ${account.username} has expired. Sign in again to use it.`);
      return;
    }
    const interrupts = listInterruptions({ voiceSession, activeGame: getActiveGame() });
    if (interrupts.length) {
      setConfirm({ kind: 'switch', account, message: `Switching to ${account.username} will end ${sentence(interrupts)}.` });
      return;
    }
    doSwitch(account);
  };

  const doRemove = async (account) => {
    setBusy(`remove:${account.user_id}`);
    try {
      const res = await removeAccount(account.user_id);
      if (res.signed_out) {
        announceAccountChange(null);
        restartApp('/comms');
        return;
      }
      setConfirm(null);
      setBusy('');
      load();
    } catch (e) {
      setBusy('');
      setError(e.message || 'Could not remove the account.');
    }
  };

  const doSignOutAll = async () => {
    setBusy('signout-all');
    try {
      await signOutAllAccounts();
    } catch {}
    announceAccountChange(null);
    restartApp('/comms');
  };

  const doSignOut = async () => {
    const interrupts = listInterruptions({ voiceSession, activeGame: getActiveGame() });
    if (interrupts.length && !confirm) {
      setConfirm({ kind: 'signout', message: `Signing out will end ${sentence(interrupts)}.` });
      return;
    }
    setBusy('signout');
    try {
      await fetch('/auth/logout', { method: 'GET', credentials: 'include' });
    } catch {}
    announceAccountChange(null);
    restartApp('/comms');
  };

  const makeDefault = async (account) => {
    setRowMenu(null);
    try {
      await setDefaultAccount(account.user_id);
      load();
    } catch (e) {
      setError(e.message || 'Could not set the default account.');
    }
  };

  if (!open) return null;

  const currentName = currentUser && (currentUser.username || currentUser.name);

  const panel = (
    <div
      ref={panelRef}
      className="vx-acct-panel vx-pz"
      role="dialog"
      aria-modal="false"
      aria-label="Accounts"
      data-testid="account-switcher"
      style={{ left: pos.left, top: pos.top == null ? undefined : pos.top, bottom: pos.bottom == null ? undefined : pos.bottom, width: pos.width }}
    >
      <div className="vx-acct-head">
        <div className="vx-acct-title">Accounts on this device</div>
        <div className="vx-acct-sub">Signed in as {currentName || 'you'}</div>
        <QuickStatus onOpenSettings={onOpenStatusSettings} />
      </div>

      {error ? (
        <div className="vx-acct-error" role="alert">
          {error}
        </div>
      ) : null}

      {confirm ? (
        <div className="vx-acct-confirm" role="alertdialog" aria-label="Confirm">
          <div>{confirm.message || ''}</div>
          {confirm.kind === 'remove' ? (
            <div className="vx-acct-sub">
              This only removes {confirm.account.username} from this device. The account is not deleted, and you can sign in again at any time.
            </div>
          ) : null}
          {confirm.kind === 'signout-all' ? <div className="vx-acct-sub">Every account saved on this device will need to sign in again.</div> : null}
          <div className="vx-acct-actions">
            <button type="button" className="vx-btn small" onClick={() => setConfirm(null)}>
              Cancel
            </button>
            <button
              type="button"
              className={`vx-btn small ${confirm.kind === 'switch' ? 'primary' : 'danger'}`}
              disabled={!!busy}
              data-testid="account-confirm"
              onClick={() => {
                if (confirm.kind === 'switch') doSwitch(confirm.account);
                else if (confirm.kind === 'remove') doRemove(confirm.account);
                else if (confirm.kind === 'signout-all') doSignOutAll();
                else if (confirm.kind === 'signout') doSignOut();
              }}
            >
              {confirm.kind === 'switch' ? 'Switch anyway' : confirm.kind === 'remove' ? 'Remove from device' : 'Sign out'}
            </button>
          </div>
        </div>
      ) : null}

      <ul className="vx-acct-list" aria-label="Saved accounts">
        {accounts === null ? <li className="vx-acct-loading">Loading…</li> : null}
        {(accounts || []).map((a) => (
          <li key={a.user_id} className={`vx-acct-row${a.is_current ? ' is-current' : ''}`}>
            <button
              type="button"
              className="vx-acct-main"
              onClick={() => requestSwitch(a)}
              aria-current={a.is_current ? 'true' : undefined}
              disabled={busy === `switch:${a.user_id}`}
              data-testid="account-row"
              data-user-id={a.user_id}
              aria-label={a.is_current ? `${a.username}, active account` : a.expired ? `${a.username}, sign-in expired` : `Switch to ${a.username}`}
            >
              <AccountAvatar url={a.avatar_url} name={a.username} />
              <span className="vx-acct-name">
                <span>{a.username}</span>
                <span className="vx-acct-badges">
                  {a.is_current ? <span className="vx-acct-badge on">Active</span> : null}
                  {a.is_default ? <span className="vx-acct-badge">Default</span> : null}
                  {a.expired ? <span className="vx-acct-badge warn">Signed out</span> : null}
                </span>
              </span>
              {busy === `switch:${a.user_id}` ? <span className="vx-acct-sub">Switching…</span> : null}
            </button>
            <button
              type="button"
              className="vx-icon-btn"
              aria-haspopup="menu"
              aria-expanded={rowMenu === a.user_id}
              aria-label={`Options for ${a.username}`}
              data-testid="account-row-menu"
              onClick={() => setRowMenu((v) => (v === a.user_id ? null : a.user_id))}
            >
              ⋯
            </button>
            {rowMenu === a.user_id ? (
              <div className="vx-acct-rowmenu" role="menu">
                {a.expired ? (
                  <LoginButton className="vx-menu-item" redirectPath="/comms" role="menuitem">
                    Sign in again
                  </LoginButton>
                ) : null}
                {!a.is_default ? (
                  <button type="button" role="menuitem" className="vx-menu-item" onClick={() => makeDefault(a)} data-testid="account-make-default">
                    Make default on this device
                  </button>
                ) : null}
                <button
                  type="button"
                  role="menuitem"
                  className="vx-menu-item"
                  data-testid="account-remove"
                  onClick={() => {
                    setRowMenu(null);
                    setConfirm({ kind: 'remove', account: a, message: `Remove ${a.username} from this device?` });
                  }}
                >
                  Remove from this device
                </button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>

      <div className="vx-acct-foot">
        <LoginButton className="vx-btn small" redirectPath="/comms" data-testid="account-add">
          + Add another account
        </LoginButton>
        <div className="vx-acct-foot-row">
          <button
            type="button"
            className="vx-btn small"
            onClick={() => {
              onClose();
              if (typeof onOpenSettings === 'function') onOpenSettings();
            }}
          >
            Signed-in devices
          </button>
          <button type="button" className="vx-btn small danger" onClick={doSignOut} disabled={busy === 'signout'} data-testid="account-signout">
            Sign out
          </button>
        </div>
        {(accounts || []).length > 1 ? (
          <button
            type="button"
            className="vx-link-btn"
            onClick={() => setConfirm({ kind: 'signout-all', message: 'Sign out of every account on this device?' })}
            data-testid="account-signout-all"
          >
            Sign out of all accounts on this device
          </button>
        ) : null}
      </div>
    </div>
  );

  return createPortal(panel, document.body);
}

// Signed-out screen: resume an account still signed in on this device.
export function SavedAccountsPicker() {
  const [accounts, setAccounts] = useState([]);
  const [error, setError] = useState('');
  useEffect(() => {
    let alive = true;
    listAccounts()
      .then((res) => alive && setAccounts((res.accounts || []).filter((a) => !a.expired)))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  if (!accounts.length) return null;
  return (
    <div className="vx-saved-accounts vx-pz" data-testid="saved-accounts">
      <div className="vx-acct-title">Continue as</div>
      {error ? (
        <div className="vx-acct-error" role="alert">
          {error}
        </div>
      ) : null}
      {accounts.map((a) => (
        <button
          key={a.user_id}
          type="button"
          className="vx-acct-main"
          onClick={async () => {
            try {
              await switchAccount(a.user_id);
              announceAccountChange(a.user_id);
              restartApp('/comms');
            } catch (e) {
              setError(e.message || 'Could not sign in with that account.');
            }
          }}
        >
          <AccountAvatar url={a.avatar_url} name={a.username} />
          <span className="vx-acct-name">
            <span>{a.username}</span>
            {a.is_default ? <span className="vx-acct-badges"><span className="vx-acct-badge">Default</span></span> : null}
          </span>
        </button>
      ))}
    </div>
  );
}
